<template>
  <b-modal
    v-model="modal"
    centered
    title="Sign Up"
    modal-class="modal-bb-squared"
    hide-footer
    v-on="$listeners"
  >
    <template slot="modal-header" slot-scope="{ close }">
      <h5>Claim your investor profile</h5>
      <p class="text-gray-700 pt-16">
        Claim your investor profile and get full access to The Hub's
        functionalities as an investor and gain ownership of your profile. Your
        information will only be used for internal purposes to verify and if
        needed, contact you.
      </p>

      <button class="close" @click="close()"></button>
    </template>

    <template slot="default">
      <AlertSlideUpDown :show="!!promptErrorMessage">{{
        promptErrorMessage
      }}</AlertSlideUpDown>

      <form @submit.prevent="onSubmit">
        <b-form-row>
          <b-col sm="6">
            <FormItem
              class="mb-10"
              invalid-message="Please enter first name"
              :state="
                $v.form.name.first.$dirty ? !$v.form.name.first.$error : null
              "
              required
            >
              <b-form-input
                v-model="form.name.first"
                maxlength="50"
                :state="
                  $v.form.name.first.$dirty ? !$v.form.name.first.$error : null
                "
                placeholder="First name"
              />
            </FormItem>
          </b-col>

          <b-col>
            <FormItem
              class="mb-10"
              invalid-message="Please enter last name"
              :state="
                $v.form.name.last.$dirty ? !$v.form.name.last.$error : null
              "
              required
            >
              <b-form-input
                v-model="form.name.last"
                maxlength="50"
                :state="
                  $v.form.name.last.$dirty ? !$v.form.name.last.$error : null
                "
                placeholder="Last name"
              />
            </FormItem>
          </b-col>
        </b-form-row>

        <FormItem
          class="mb-10"
          invalid-message="Please enter your email address"
          :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
          required
        >
          <b-form-input
            v-model="form.email"
            maxlength="50"
            :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
            placeholder="Email"
          />
        </FormItem>

        <FormItem
          class="mb-10"
          invalid-message="Please enter your LinkedIn"
          :state="$v.form.linkedin.$dirty ? !$v.form.linkedin.$error : null"
          required
        >
          <b-form-input
            v-model="form.linkedin"
            maxlength="150"
            :state="$v.form.linkedin.$dirty ? !$v.form.linkedin.$error : null"
            placeholder="LinkedIn"
          />
        </FormItem>

        <FormItem
          class="mb-10"
          invalid-message="Please enter relation with this company"
          :state="
            $v.form.description.$dirty ? !$v.form.description.$error : null
          "
          required
        >
          <template slot="description">
            <FormInputLengthCounter
              label="Add a text between 0 and 500 characters"
              :min="0"
              :max="500"
              :text="form.description"
            />
          </template>

          <b-form-textarea
            v-model="form.description"
            :rows="5"
            :max-rows="10"
            placeholder="Tell us your relation with this company"
            @blur="$v.form.description.$touch()"
          />
        </FormItem>

        <FormItem
          :state="
            $v.form.reCaptchaResponse.$dirty
              ? !$v.form.reCaptchaResponse.$error
              : null
          "
          invalid-message="Please verify you are not a robot"
        >
          <VueRecaptcha
            ref="reCaptcha"
            :sitekey="siteKey"
            :load-recaptcha-script="true"
            @verify="onVerify"
            @expired="onExpired"
          />
        </FormItem>

        <button type="submit" class="btn btn-lg w-100 mb-16">
          <span v-if="!isRequesting">Claim investor profile</span>
          <b-spinner v-else small />
        </button>

        <div class="fs14 mt-10">
          By sending the request you confirm that you accept our
          <router-link to="/terms" target="_blank" class="text-blue-900"
            >Terms of Service</router-link
          >
          and
          <router-link
            to="/privacy-policy"
            target="_blank"
            class="text-blue-900"
            >Privacy Policy</router-link
          >.
        </div>
      </form>
    </template>
  </b-modal>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import { validationMixin } from 'vuelidate'
import { linkedinUrlValidator } from '@/validators/urlValidator'
import { required, maxLength, email } from 'vuelidate/lib/validators'
import cloneDeep from 'lodash/cloneDeep'
import FormItem from '@/components/form/FormItem/index'
import AlertSlideUpDown from '@/components/AlertSlideUpDown'
import FormInputLengthCounter from '@/components/form/FormInputLengthCounter'

export default {
  name: 'FundingClaimModal',
  components: {
    AlertSlideUpDown,
    FormItem,
    FormInputLengthCounter,
    VueRecaptcha
  },
  mixins: [validationMixin],
  props: {
    value: {
      type: Boolean,
      required: true
    },
    investor: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      siteKey: process.env.GOOGLE_RECAPTCHA_SITE_KEY,
      promptErrorMessage: undefined,
      isRequesting: false,
      form: {
        name: {
          first: undefined,
          last: undefined
        },
        email: undefined,
        linkedin: undefined,
        description: '',
        reCaptchaResponse: undefined
      }
    }
  },
  computed: {
    modal: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },
  methods: {
    async onSubmit() {
      if (this.isRequesting) {
        return
      }

      this.$v.form.$touch() // will trigger errors to appear.

      if (this.$v.form.$error) {
        return
      }

      this.isRequesting = true

      try {
        await this.$axios.post(
          `investors/${this.investor.key}/claim`,
          cloneDeep(this.form)
        )

        this.$emit('submitted')
      } catch (err) {
        this.promptErrorMessage = err.message
      }

      if (this.$refs.reCaptcha) {
        this.$refs.reCaptcha.reset()
      }

      this.form.reCaptchaResponse = undefined

      this.isRequesting = false
    },
    onVerify(response) {
      this.form.reCaptchaResponse = response
      this.$v.form.reCaptchaResponse.$touch()
    },
    onExpired() {
      this.form.reCaptchaResponse = undefined
      this.$v.form.reCaptchaResponse.$touch()
    }
  },
  validations: {
    form: {
      email: {
        required,
        email
      },
      name: {
        first: {
          required
        },
        last: {
          required
        }
      },
      linkedin: {
        required,
        linkedinUrlValidator
      },
      description: {
        maxLength: maxLength(300)
      },
      reCaptchaResponse: {
        required
      }
    }
  }
}
</script>
